import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CategoryTable from "./category-table";
import CategoryCard from "./category-card/category-card";
import ApiCategoryManagement from "../../../api/category-managements";
import useNotification from "../../../components/notification/notification";
import "./styles.css";
import { Body } from "../../../components/main-body/body";
import { useForm } from "antd/es/form/Form";
import { Modal } from "antd";
import debounce from "../../../common/utils/debounce";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import { useRef } from "react";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { useCan } from "../../../common/utils/usePermissions";

const DeleteModalComponent = ({
  item,
  handleOk,
  handleCancel,
  isModalOpen,
}) => {
  return (
    <Modal
      title={item?.name}
      open={isModalOpen}
      onOk={() => {
        handleOk(item.id, item.name);
      }}
      okText="Delete"
      onCancel={handleCancel}
      okButtonProps={{ danger: true, "data-testid": "category-delete-confirm" }}
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this category?</p>
    </Modal>
  );
};

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [editMode, setEditMode] = useState();
  const [selectedCategory, setSelectedCategory] = useState({});
  const [form] = useForm();
  const { t } = useTranslation();
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const { openNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } =
    useBulkSelection(categories, { rowKey: "id" });

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;

      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiCategoryManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};

      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        openNotification({
          type: "success",
          message: (
            <p>
              {deletedIds.length}{" "}
              {deletedIds.length > 1 ? "categories were" : "category was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        openNotification({
          type: "error",
          message: (
            <p>
              Unable to delete {failedIds.length}{" "}
              {failedIds.length > 1 ? "categories" : "category"}
            </p>
          ),
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      getCategories();
    } catch (e) {
      openNotification({
        type: "error",
        message: <p>Failed to delete categories</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    clearSelection,
    setSelectedKeys,
    openNotification,
  ]);

  const openCategoryDrawer = (edit) => {
    if (selectedCategory?.name) {
      setSelectedCategory({});
    }
    if (edit) {
      setEditMode(true);
    }
    setOpen(true);
  };

  const closeCategoryDrawer = () => {
    setSelectedCategory({});
    setEditMode(false);
    setOpen(false);
  };

  const getCategories = (deselect, query = "") => {
    ApiCategoryManagement.getCategories({query}).then(({ data }) => {
      const { categories } = data || {};

      setCategories(categories);
    });
  };

  useAdminEvents({
    onCategoryChanged: (payload) => {
      getCategories(false, searchValue);

      setSelectedCategory((prev) => {
        if (!prev?.id) return prev;

        const changedIds = payload?.ids || [payload?.id].filter(Boolean);
        if (!changedIds.includes(prev.id)) return prev;

        if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
          closeCategoryDrawer();
          return {};
        }

        return payload?.category ? { ...prev, ...payload.category } : prev;
      });
    },
  });

  const debouncedGetCategories = useCallback(debounce(getCategories, 400), [{}, selectedCategory.id]);

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    debouncedGetCategories(false, value);
  };

  const headerProps = useMemo(
    () => ({
      title: "Category management",
      create: canCreate && {
        testID: "category-create",
        label: t("administration.category.create"),
        onClick: () => openCategoryDrawer(true),
      },
      search: {
        value: searchValue,
        onHandleChange: handleUpdateSearchValue,
      },
    }),
    [isOpen, selectedCategory, canCreate]
  );

  useEffect(() => {
    getCategories();
  }, []);

  const createCategory = (category) => {
    closeCategoryDrawer();
    ApiCategoryManagement.addCategory(category)
      .then(() => {
        getCategories();
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.category.notification.created", {
                category: category.name,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                `administration.category.notification.${
                  e?.response?.data?.error?.code || "error"
                }`
              )}
            </p>
          ),
        });
      });
  };

  const updateCategory = (id, category) => {
    closeCategoryDrawer();
    ApiCategoryManagement.updateCategory(id, category)
      .then(() => {
        getCategories();
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.category.notification.updated", {
                category: category.name,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                `administration.category.notification.${
                  e?.response?.data?.error?.code || "error"
                }`
              )}
            </p>
          ),
        });
      });
  };

  const onHandleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const deleteCategory = (id, name) => {
    setIsDeleteModalOpen(false);
    closeCategoryDrawer();
    ApiCategoryManagement.deleteCategory(id)
      .then(() => {
        getCategories();
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.category.notification.deleted", {
                category: name,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                `administration.category.notification.${
                  e?.response?.data?.error || "error"
                }`
              )}
            </p>
          ),
        });
      });
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    setOpen(true);
  };

  const toEditMode = () => {
    setEditMode(true);
  };

  useEffect(() => {
    form.resetFields();
  }, [selectedCategory, form]);

  return (
    <Body header={headerProps}>
    <CategoryTable
      data={categories}
      onHandleRowClick={handleSelectCategory}
      selectionColumn={getSelectionColumn()}
      containerRef={tableRef}
      after={
        <BulkSelectionBar
          left={bulkLeft}
          count={selectedKeys.length}
          onClear={clearSelection}
          onDelete={handleBulkDelete}
          loading={isBulkDeleting}
        />
      }
    />
      <CategoryCard
        category={selectedCategory}
        isOpen={isOpen}
        close={closeCategoryDrawer}
        editMode={editMode}
        toEditMode={toEditMode}
        createCategory={createCategory}
        updateCategory={updateCategory}
        deleteCategory={onHandleDelete}
        form={form}
      />
      {
        <DeleteModalComponent
          item={selectedCategory}
          isModalOpen={isDeleteModalOpen}
          handleCancel={onCloseDeleteModal}
          handleOk={deleteCategory}
        />
      }
    </Body>
  );
};

export default CategoryManagement;
